import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { transcriptsApi } from '../../api/transcripts'
import { RefusedError } from '../../api/client'
import { useSpoke } from '../../hooks/useSpoke'
import { formatDate } from '../../lib/format'
import { dueLabel, speakerSlot } from '../../lib/transcript'
import {
  REQUIREMENT_PRIORITIES, REQUIREMENT_STATUSES, TAKEAWAY_STATUSES,
  type PatchRequirementBody, type PatchTakeawayBody, type TakeawayView, type TranscriptLedgerView, type TranscriptRequirement,
} from '../../types/transcripts'
import { TranscriptText, type TextFocus, type TextMark } from './TranscriptText'
import './transcripts.css'

// One transcript: the meeting in its context, the ledger read out of it (requirements in EARS form, each with the
// words and timecode it came from), the takeaways board (ours and theirs, each with its status, owner, and the date
// promised — and how late it is), and the transcript itself alongside with every quoted span marked in place.

type BoardFilter = 'open' | 'all' | 'closed'

export function TranscriptPage() {
  const { transcriptId = '' } = useParams<{ transcriptId: string }>()
  const spoke = useSpoke()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const ledger = useQuery({ queryKey: ['transcript-ledger', spoke, transcriptId], queryFn: () => transcriptsApi.ledger(spoke, transcriptId), retry: false })
  const full = useQuery({ queryKey: ['transcript', spoke, transcriptId], queryFn: () => transcriptsApi.get(spoke, transcriptId), retry: false })
  const extract = useMutation({ mutationFn: () => transcriptsApi.extract(spoke, transcriptId), onSuccess: (r) => navigate(`/jobs/${encodeURIComponent(r.job.id)}`) })
  const invalidate = () => { void qc.invalidateQueries({ queryKey: ['transcript-ledger', spoke, transcriptId] }); void qc.invalidateQueries({ queryKey: ['transcripts-ledger', spoke] }) }
  const patchTakeaway = useMutation({ mutationFn: ({ id, body }: { id: string; body: PatchTakeawayBody }) => transcriptsApi.patchTakeaway(spoke, id, body), onSuccess: invalidate })
  const patchRequirement = useMutation({ mutationFn: ({ id, body }: { id: string; body: PatchRequirementBody }) => transcriptsApi.patchRequirement(spoke, id, body), onSuccess: invalidate })
  const [focus, setFocus] = useState<TextFocus | null>(null)
  const [board, setBoard] = useState<BoardFilter>('open')
  const [copied, setCopied] = useState(false)

  const v = ledger.data
  const marks = useMemo<TextMark[]>(() => v ? [
    ...v.requirements.map((r) => ({ id: r.requirementId, quote: r.quote, timecode: r.timecode, kind: 'requirement' as const, label: `${r.code} · ${r.text}` })),
    ...v.takeaways.map((t) => ({ id: t.takeaway.takeawayId, quote: t.takeaway.quote, timecode: t.takeaway.timecode, kind: t.takeaway.kind, label: `${t.takeaway.kind === 'ours' ? 'We owe' : 'They owe'} · ${t.takeaway.text}` })),
  ] : [], [v])
  const jumpTo = (kind: 'tc' | 'q', id: string) => setFocus({ kind, id, nonce: Date.now() })
  const highlight = (id: string) => {
    const el = document.getElementById(`item-${id}`)
    if (!el) return
    el.scrollIntoView({ block: 'center', behavior: 'smooth' })
    el.classList.add('flash')
    window.setTimeout(() => el.classList.remove('flash'), 1600)
  }
  const refusal = ledger.error instanceof RefusedError ? ledger.error.refusal : null
  const patchError = patchTakeaway.error instanceof RefusedError ? patchTakeaway.error.refusal.message : patchTakeaway.error?.message
    ?? (patchRequirement.error instanceof RefusedError ? patchRequirement.error.refusal.message : patchRequirement.error?.message)

  if (refusal) {
    return (
      <section className="page transcripts">
        <p className="kicker"><Link to="/transcripts">Transcripts</Link></p>
        <h1>No such transcript</h1>
        <div className="refusal"><strong>{refusal.reason}</strong><p>{refusal.message}</p></div>
      </section>
    )
  }
  if (!v) return <p className="muted centered">Loading…</p>

  const t = v.transcript
  const open = v.takeaways.filter((x) => x.takeaway.status === 'open' || x.takeaway.status === 'in-progress')
  const ours = open.filter((x) => x.takeaway.kind === 'ours'), theirs = open.filter((x) => x.takeaway.kind === 'theirs')
  const overdue = open.filter((x) => x.due.state === 'overdue')
  const unassigned = ours.filter((x) => !x.takeaway.assigneeUserId)
  const shown = v.takeaways.filter((x) => board === 'all' ? true : board === 'open' ? (x.takeaway.status === 'open' || x.takeaway.status === 'in-progress') : (x.takeaway.status === 'done' || x.takeaway.status === 'dropped'))
  const copyPath = async (path: string) => { try { await navigator.clipboard.writeText(path); setCopied(true); window.setTimeout(() => setCopied(false), 1500) } catch { /* no clipboard */ } }

  return (
    <section className="page transcripts transcript-page">
      <header className="voice-head">
        <div>
          <p className="kicker"><Link to="/transcripts">Transcripts</Link> · {t.project} · {formatDate(t.date)}{v.account?.companyName ? ' · ' : ''}{v.account?.companyId && <Link to={`/accounts/${encodeURIComponent(v.account.companyId)}`}>{v.account.companyName}</Link>}</p>
          <h1>{t.meeting}</h1>
          <p className="lede">
            {t.wordCount.toLocaleString()} words, {t.speakers.length} in the room.{' '}
            {v.extractedAt ? <>Read for its ledger {formatDate(v.extractedAt)}: {v.requirements.length} requirements, {v.takeaways.length} takeaways.</> : <>Not yet read for its ledger.</>}
          </p>
          <div className="tx-facts">
            {t.meetingId && <Link className="chip on" to="/meetings">On the calendar</Link>}
            {t.speakers.map((s) => <span key={s} className={`speaker-chip${v.ourSpeakers.includes(s) ? ' ours' : ''}`} style={{ ['--speaker' as string]: `var(--speaker-${speakerSlot(t.speakers, s)})` }}><i />{s}{v.ourSpeakers.includes(s) && <em>ours</em>}</span>)}
            {v.videoPath && <button className="chip path" title="Copy the recording's path" onClick={() => void copyPath(v.videoPath!)}><span className="mono">{v.videoPath}</span>{copied ? ' · copied' : ''}</button>}
          </div>
        </div>
        <div className="actions">
          {v.activeJobId && <Link className="button" to={`/jobs/${encodeURIComponent(v.activeJobId)}`}>Watch the read</Link>}
          <button className="button primary" onClick={() => extract.mutate()} disabled={extract.isPending || !!v.activeJobId || !v.modelConfigured} title={v.modelConfigured ? 'The model reads this transcript for its requirements and takeaways. Your edits are kept.' : 'No model is configured on this deployment.'}>
            {extract.isPending ? 'Starting…' : v.extractedAt ? 'Read again' : 'Read this transcript'}
          </button>
          <Link className="button" to="/transcripts">All transcripts</Link>
        </div>
      </header>
      {extract.isError && <p className="error">{extract.error instanceof RefusedError ? extract.error.refusal.message : extract.error.message}</p>}
      {patchError && <p className="error">{patchError}</p>}

      <section className="glance ledger-glance one">
        <div className="glance-owed">
          <p className="kicker">This meeting, at a glance</p>
          <p className="glance-big"><strong>{open.length}</strong><span>open takeaways · {ours.length} we owe, {theirs.length} they owe</span></p>
          <div className="glance-callouts">
            {overdue.length > 0 && <button className="callout danger" onClick={() => { setBoard('open'); highlight(overdue[0].takeaway.takeawayId) }}><strong>{overdue.length}</strong> past the date promised</button>}
            {unassigned.length > 0 && <button className="callout warn" onClick={() => { setBoard('open'); highlight(unassigned[0].takeaway.takeawayId) }}><strong>{unassigned.length}</strong> of ours with no owner</button>}
            {open.length === 0 && v.takeaways.length > 0 && <span className="callout ok"><strong>0</strong> open — everything from this meeting is closed</span>}
            {!v.extractedAt && <span className="callout"><strong>Unread</strong> press Read to build the ledger</span>}
          </div>
        </div>
        <div className="glance-priorities">
          <p className="kicker">Requirements by priority</p>
          <PriorityBars requirements={v.requirements} />
        </div>
        <div className="glance-stats">
          <div className="glance-stat"><strong>{v.requirements.length}</strong><span>requirements</span></div>
          <div className="glance-stat"><strong>{v.requirements.filter((r) => r.status === 'accepted').length}</strong><span>accepted</span></div>
          <div className="glance-stat ours"><strong>{ours.length}</strong><span>we owe</span></div>
          <div className="glance-stat theirs"><strong>{theirs.length}</strong><span>they owe</span></div>
          <div className={`glance-stat${overdue.length > 0 ? ' hot' : ''}`}><strong>{overdue.length}</strong><span>overdue</span></div>
          <div className="glance-stat"><strong>{v.takeaways.filter((x) => x.takeaway.status === 'done').length}</strong><span>done</span></div>
        </div>
      </section>

      <div className="tx-body">
        <div className="tx-ledger">
          {!v.extractedAt && v.requirements.length === 0 && v.takeaways.length === 0 && (
            <div className="card empty-ledger">
              <h2>No ledger yet</h2>
              <p className="muted">Reading this transcript lists every requirement the client stated (in EARS form) and every promise made in the room, each with the words and the timecode it came from. It runs as a job you can watch; your later edits are never overwritten by a re-read.</p>
              <button className="button primary" onClick={() => extract.mutate()} disabled={extract.isPending || !!v.activeJobId || !v.modelConfigured}>{extract.isPending ? 'Starting…' : 'Read this transcript'}</button>
            </div>
          )}

          <div className="card ledger-card">
            <div className="card-head">
              <div>
                <h2>Requirements ledger</h2>
                <p className="muted">{v.requirements.length === 0 ? 'Nothing on the ledger.' : `${v.requirements.length} requirements, in EARS form, each read from the words beside it. Set priority and status here; the model never overwrites what you set.`}</p>
              </div>
            </div>
            {v.requirements.length > 0 && (
              <table className="grid req-grid">
                <thead><tr><th>Code</th><th>Priority</th><th>Requirement</th><th>From the room</th><th>Status</th></tr></thead>
                <tbody>
                  {v.requirements.map((r) => <RequirementRow key={r.requirementId} r={r} speakers={t.speakers} ours={v.ourSpeakers} onPatch={(body) => patchRequirement.mutate({ id: r.requirementId, body })} onJump={jumpTo} />)}
                </tbody>
              </table>
            )}
          </div>

          <div className="card board-card">
            <div className="card-head">
              <div>
                <h2>Takeaways</h2>
                <p className="muted">What was promised in the room, who owes it, who on our side owns it, and by when. A change here is recorded on the account.</p>
              </div>
              <div className="tabs board-tabs">
                <button className={`tab ${board === 'open' ? 'active' : ''}`} onClick={() => setBoard('open')}>Open <span className="count">{open.length}</span></button>
                <button className={`tab ${board === 'closed' ? 'active' : ''}`} onClick={() => setBoard('closed')}>Closed <span className="count">{v.takeaways.length - open.length}</span></button>
                <button className={`tab ${board === 'all' ? 'active' : ''}`} onClick={() => setBoard('all')}>All <span className="count">{v.takeaways.length}</span></button>
              </div>
            </div>
            {v.takeaways.length === 0 ? <p className="muted">No takeaways on the ledger.</p> : (
              <div className="board">
                <BoardColumn title="We owe" kind="ours" items={shown.filter((x) => x.takeaway.kind === 'ours')} view={v} onPatch={(id, body) => patchTakeaway.mutate({ id, body })} onJump={jumpTo} />
                <BoardColumn title="They owe" kind="theirs" items={shown.filter((x) => x.takeaway.kind === 'theirs')} view={v} onPatch={(id, body) => patchTakeaway.mutate({ id, body })} onJump={jumpTo} />
              </div>
            )}
          </div>
        </div>

        <aside className="card tx-panel">
          <div className="card-head">
            <div>
              <h2>The transcript</h2>
              <p className="muted">{t.clipName} · every quoted span is marked in its speaker's colour; a mark lands on its line.</p>
            </div>
          </div>
          {full.isPending && <p className="muted">Loading…</p>}
          {full.data && <TranscriptText text={full.data.text} speakers={t.speakers} ourSpeakers={v.ourSpeakers} marks={marks} focus={focus} onMark={highlight} />}
        </aside>
      </div>
    </section>
  )
}

function PriorityBars({ requirements }: { requirements: TranscriptRequirement[] }) {
  const n = (p: string) => requirements.filter((r) => r.priority === p).length
  const max = Math.max(1, ...REQUIREMENT_PRIORITIES.map(n))
  if (requirements.length === 0) return <p className="muted">None read yet.</p>
  return (
    <ul className="prio-bars">
      {REQUIREMENT_PRIORITIES.map((p) => (
        <li key={p} className={p}><span className="prio-label">{p}</span><span className="prio-bar"><span style={{ width: `${(n(p) / max) * 100}%` }} /></span><span className="prio-n">{n(p)}</span></li>
      ))}
    </ul>
  )
}

function Quote({ quote, timecode, speaker, speakers, ours, onJump, id }: { quote: string; timecode: string | null; speaker: string | null; speakers: string[]; ours: string[]; onJump: (kind: 'tc' | 'q', id: string) => void; id: string }) {
  const slot = speakerSlot(speakers, speaker)
  const isOurs = !!speaker && ours.includes(speaker)
  return (
    <blockquote className="from-room" style={{ ['--speaker' as string]: `var(--speaker-${slot})` }}>
      {quote ? <button className="quote-text" title="Find these words in the transcript" onClick={() => onJump('q', id)}>“{quote}”</button> : <span className="muted small">no words captured</span>}
      <span className="quote-meta">
        {speaker && <span className="speaker-chip"><i />{speaker}{isOurs && <em>ours</em>}</span>}
        {timecode && <button className="timecode" title="Jump to this moment in the transcript" onClick={() => onJump('tc', timecode)}>{timecode}</button>}
      </span>
    </blockquote>
  )
}

function RequirementRow({ r, speakers, ours, onPatch, onJump }: { r: TranscriptRequirement; speakers: string[]; ours: string[]; onPatch: (b: PatchRequirementBody) => void; onJump: (kind: 'tc' | 'q', id: string) => void }) {
  const [editing, setEditing] = useState(false)
  const [text, setText] = useState(r.text)
  return (
    <tr id={`item-${r.requirementId}`} className={`req-row ${r.priority} ${r.status}`}>
      <td className="mono code">{r.code}</td>
      <td>
        <select className={`prio ${r.priority}`} value={r.priority} onChange={(e) => onPatch({ priority: e.target.value as TranscriptRequirement['priority'] })} aria-label="Priority">
          {REQUIREMENT_PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
      </td>
      <td className="req-text">
        {editing ? (
          <div className="edit-box">
            <textarea value={text} rows={3} onChange={(e) => setText(e.target.value)} />
            <span className="actions"><button className="button small primary" onClick={() => { onPatch({ text }); setEditing(false) }}>Save</button><button className="button small" onClick={() => { setText(r.text); setEditing(false) }}>Cancel</button></span>
          </div>
        ) : (
          <><span className="ears">{r.text}</span><button className="link-button edit" onClick={() => setEditing(true)}>edit</button>{r.editedAt && <span className="tag edited">edited</span>}</>
        )}
      </td>
      <td className="quote-cell"><Quote id={r.requirementId} quote={r.quote} timecode={r.timecode} speaker={r.speaker} speakers={speakers} ours={ours} onJump={onJump} /></td>
      <td>
        <select className={`status ${r.status}`} value={r.status} onChange={(e) => onPatch({ status: e.target.value as TranscriptRequirement['status'] })} aria-label="Status">
          {REQUIREMENT_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </td>
    </tr>
  )
}

function BoardColumn({ title, kind, items, view, onPatch, onJump }: { title: string; kind: 'ours' | 'theirs'; items: TakeawayView[]; view: TranscriptLedgerView; onPatch: (id: string, b: PatchTakeawayBody) => void; onJump: (kind: 'tc' | 'q', id: string) => void }) {
  return (
    <div className={`board-col ${kind}`}>
      <h3><i className={`dot ${kind}`} />{title} <span className="count">{items.length}</span></h3>
      {items.length === 0 && <p className="muted small">Nothing here.</p>}
      {items.map((x) => <TakeawayCard key={x.takeaway.takeawayId} v={x} view={view} onPatch={(b) => onPatch(x.takeaway.takeawayId, b)} onJump={onJump} />)}
    </div>
  )
}

function TakeawayCard({ v, view, onPatch, onJump }: { v: TakeawayView; view: TranscriptLedgerView; onPatch: (b: PatchTakeawayBody) => void; onJump: (kind: 'tc' | 'q', id: string) => void }) {
  const t = v.takeaway
  const [editing, setEditing] = useState(false)
  const [text, setText] = useState(t.text)
  const isOpen = t.status === 'open' || t.status === 'in-progress'
  const unowned = t.kind === 'ours' && isOpen && !t.assigneeUserId
  const state = v.due.state
  return (
    <article id={`item-${t.takeawayId}`} className={`takeaway ${t.kind} ${t.status} due-${state}${unowned ? ' unowned' : ''}`}>
      <header className="tk-head">
        <select className={`status ${t.status}`} value={t.status} onChange={(e) => onPatch({ status: e.target.value as typeof t.status })} aria-label="Status">
          {TAKEAWAY_STATUSES.map((s) => <option key={s} value={s}>{s.replace('-', ' ')}</option>)}
        </select>
        <span className="tk-promised">{t.kind === 'ours' ? 'we promised' : 'they promised'}{v.promisedByName ? <> · <strong>{v.promisedByName}</strong></> : ''}</span>
        {t.editedAt && <span className="tag edited">edited</span>}
      </header>
      {editing ? (
        <div className="edit-box">
          <textarea value={text} rows={2} onChange={(e) => setText(e.target.value)} />
          <span className="actions"><button className="button small primary" onClick={() => { onPatch({ text }); setEditing(false) }}>Save</button><button className="button small" onClick={() => { setText(t.text); setEditing(false) }}>Cancel</button></span>
        </div>
      ) : (
        <p className="tk-text">{t.text} <button className="link-button edit" onClick={() => setEditing(true)}>edit</button></p>
      )}
      <Quote id={t.takeawayId} quote={t.quote} timecode={t.timecode} speaker={t.speaker} speakers={view.transcript.speakers} ours={view.ourSpeakers} onJump={onJump} />
      <div className="tk-controls">
        <label className="inline">
          <span>{t.kind === 'ours' ? 'Owner' : 'Chased by'}</span>
          <select value={t.assigneeUserId ?? ''} onChange={(e) => onPatch(e.target.value ? { assigneeUserId: e.target.value } : { clearAssignee: true })} aria-label="Assignee">
            <option value="">{t.kind === 'ours' ? 'nobody yet' : 'nobody'}</option>
            {view.people.map((p) => <option key={p.id} value={p.id}>{p.displayName}</option>)}
          </select>
        </label>
        <label className="inline">
          <span>Promised by</span>
          <input type="date" value={t.dueOn ?? ''} onChange={(e) => onPatch(e.target.value ? { dueOn: e.target.value } : { clearDue: true })} aria-label="Due date" />
        </label>
      </div>
      <div className="tk-callouts">
        {state === 'overdue' && <span className="callout danger"><strong>{v.due.daysLate} day{v.due.daysLate === 1 ? '' : 's'} late</strong>{dueLabel(v.due, t.dueOn, formatDate)}</span>}
        {state === 'today' && <span className="callout warn"><strong>Due today</strong>{t.dueOn ? `promised ${formatDate(t.dueOn)}` : ''}</span>}
        {state === 'ahead' && <span className="callout ok"><strong>In {v.due.daysLeft} day{v.due.daysLeft === 1 ? '' : 's'}</strong>{dueLabel(v.due, t.dueOn, formatDate)}</span>}
        {state === 'none' && isOpen && <span className="callout quiet">No date was promised</span>}
        {state === 'closed-late' && <span className="callout quiet">{dueLabel(v.due, t.dueOn, formatDate)}</span>}
        {state === 'closed-on-time' && <span className="callout ok">{dueLabel(v.due, t.dueOn, formatDate)}</span>}
        {unowned && <span className="callout warn"><strong>No owner</strong>we promised this and nobody on our side owns it</span>}
      </div>
    </article>
  )
}
